import { canv } from "@/canv/global";
import { getTarget } from "@/canv/utils";
import type { arrowTool } from "@/tools/arrowTool/arrowTool";
import type { CommentTool } from "@/tools/CommentTool/CommentTool";
import type { cardTool } from "@/tools/cardTool/cardTool";
import type { EmbedTool } from "@/tools/embedTool/embedTool";
import type { frameTool } from "@/tools/frameTool/frameTool";
import type { mindMapTool } from "@/tools/mindMapTool/mindMapTool";
import type { penTool } from "@/tools/penTool/penTool";
import type { shapeTool } from "@/tools/shapeTool/shapeTool";
import type { stickerTool } from "@/tools/stickerTool";
import type { tableTool } from "@/tools/tableTool/tableTool";
import type { textTool } from "@/tools/textTool/textTool";

export class WmFilterSubmenu {
    show() {
        const subm = document.querySelector(".wm_filter_submenu");
        if (subm) {
            subm.outerHTML = "";
            return;
        }
        const otherSubm = document.getElementsByClassName("widgetSubMenu")[0];
        if (otherSubm) {
            otherSubm.outerHTML = "";
        }

        canv.widgetMenu.subMenuBoss = "wm_filter";
        const submenu = document.createElement("div");
        submenu.classList.add("widgetSubMenu");
        submenu.classList.add("wm_filter_submenu");
        submenu.style.opacity = "0";
        document.body.prepend(submenu);

        const map = new Map();

        for (const group of canv.canvasController.selected) {
            let currentCodeName: string;

            if (group.isItemWrapper()) {
                const child = group.onlyChild();
                currentCodeName = child ? child.constructorName : "group";
            } else {
                currentCodeName = "group";
            }

            map.set(currentCodeName, (map.get(currentCodeName) ?? 0) + 1);
        }

        let template = "";

        for (const [key, value] of map) {
            template += `
                <div class="filterWidgetsWindowItem" entityType=${key}>
                    <div class="filterWidgetsWindowItemIcon">${this.getIcon(key)}</div>
                    <div class="filterWidgetsWindowItemName">${this.getName(key)}</div>
                    <div class="filterWidgetsWindowItemCount">${value}</div>
                </div>
            `;
        }

        submenu.innerHTML = template;

        setTimeout(canv.widgetMenu.TryLocateMenu.bind(canv.widgetMenu), 0);
        this.init();
    }

    init() {
        const options = document.querySelectorAll<HTMLElement>(".filterWidgetsWindowItem");

        for (let i = 0; i < options.length; i++) {
            options[i].removeEventListener("click", this.itemClick, false);
            options[i].addEventListener("click", this.itemClick, false);
        }
    }

    itemClick = (e) => {
        const itemOb = getTarget(e, "filterWidgetsWindowItem");
        const entityType = itemOb.getAttribute("entityType");

        const subMenu = document.querySelector(".widgetSubMenu");
        if (subMenu) {
            subMenu.outerHTML = "";
        }

        canv.canvasController.filterCheckedByEntityType(entityType);
    };

    getName(type: string) {
        if (type === "shape") return "Фигура";
        if (type === "text") return "Текст";
        if (type === "image") return "Изображение";
        if (type === "sketch") return "Перо";
        if (type === "arrow") return "Соединительная линия";
        if (type === "mindMap") return "Интеллект-карта";
        if (type === "sticker") return "Стикер";
        if (type === "table") return "Таблица";
        if (type === "card") return "Карточка";
        if (type === "group") return "Группа";
        if (type === "frame") return "Фрейм";
        if (type === "comment") return "Комментарий";
        if (type === "embed") return "Встраиваемый контент";
        if (type === "documentViewer") return "Документ";

        return type;
    }

    getIcon(type) {
        if (type === "shape")
            return (canv.toolBox.TryGetToolByName("shapeTool") as shapeTool | null)?.getToolView();

        if (type === "text")
            return (canv.toolBox.TryGetToolByName("textTool") as textTool | null)?.getToolView();

        if (type === "image")
            return `
            <svg width="23" height="23" viewBox="0 0 111 110" fill="none" xmlns="http://www.w3.org/2000/svg">
                <mask id="path-1-inside-1_801_6" fill="white">
                    <rect x="1" width="110" height="110" rx="5"/>
                </mask>
                <rect x="1" width="110" height="110" rx="5" fill="white" stroke="black" stroke-width="14" mask="url(#path-1-inside-1_801_6)"/>
                <path d="M3.33813 78.3964L30.9346 48.2553" stroke="black" stroke-width="7"/>
                <line x1="30.1624" y1="52.2479" x2="100.162" y2="107.248" stroke="black" stroke-width="7"/>
                <circle cx="81" cy="40" r="12" fill="white" stroke="black" stroke-width="6"/>
            </svg>
        `;

        if (type === "sketch")
            return (canv.toolBox.TryGetToolByName("penTool") as penTool | null)?.getToolView();

        if (type === "arrow")
            return (canv.toolBox.TryGetToolByName("arrowTool") as arrowTool | null)?.getToolView();

        if (type === "mindMap")
            return (
                canv.toolBox.TryGetToolByName("mindMapTool") as mindMapTool | null
            )?.getToolView();

        if (type === "sticker")
            return (
                canv.toolBox.TryGetToolByName("stickerTool") as stickerTool | null
            )?.getToolView();

        if (type === "table")
            return (canv.toolBox.TryGetToolByName("tableTool") as tableTool | null)?.getToolView();

        if (type === "card")
            return (canv.toolBox.TryGetToolByName("cardTool") as cardTool | null)?.getToolView();

        if (type === "frame")
            return (canv.toolBox.TryGetToolByName("frameTool") as frameTool | null)?.getToolView();

        if (type === "group")
            return `
            <svg width="23" height="23" viewBox="0 0 145 146" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="8" y="10" width="128" height="128" rx="3" fill="#545454"/>
                <rect x="17" y="19" width="110" height="110" rx="3" fill="white"/>
                <rect x="35" y="36" width="50" height="50" rx="1" fill="#545454"/>
                <rect x="60" y="61" width="50" height="50" rx="1" fill="#545454"/>
                <rect x="129" width="16" height="16" fill="#545454"/>
                <rect x="129" y="130" width="16" height="16" fill="#545454"/>
                <rect width="16" height="16" fill="#545454"/>
                <rect y="129" width="16" height="16" fill="#545454"/>
            </svg>
        `;

        if (type === "comment")
            return (
                canv.toolBox.TryGetToolByName("commentTool") as CommentTool | null
            )?.getToolView();

        if (type === "embed")
            return (canv.toolBox.TryGetToolByName("embedTool") as EmbedTool | null)?.getToolView();

        if (type === "documentViewer")
            return `
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11.75 1.5V4.9C11.75 5.35087 11.9344 5.78327 12.2626 6.10208C12.5908 6.42089 13.0359 6.6 13.5 6.6H17M12.625 1.5H4.75C4.28587 1.5 3.84075 1.67911 3.51256 1.99792C3.18437 2.31673 3 2.74913 3 3.2V16.8C3 17.2509 3.18437 17.6833 3.51256 18.0021C3.84075 18.3209 4.28587 18.5 4.75 18.5H15.25C15.7141 18.5 16.1592 18.3209 16.4874 18.0021C16.8156 17.6833 17 17.2509 17 16.8V5.75L12.625 1.5Z" stroke="#2C2D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            `;

        return type;
    }
}
